/* =========================================================================
   SAMSARA — SEARCH-MODAL.JS
   Sprint 4 / T8 — Predictive search modal fullscreen.

   Listens `search:open` event from header trigger Sprint 1 T4.
   Fetches Shopify Predictive Search API /search/suggest.json.
   Renders 3 sections: products / collections / queries.
   Mirror cart-drawer Sprint 1 T7 pattern (<dialog>, focus trap native, Esc).
   ========================================================================= */

(function () {
  'use strict';

  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 220;
  const RESOURCE_LIMIT = 5;

  let modal, form, input, closeBtn, results, emptyState;
  let debounceTimer = null;
  let lastQuery = '';
  let abortController = null;

  function debounce(fn, wait) {
    return function (...args) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* Predictive Search API (/search/suggest.json) intoarce pretul ca STRING in lei
     ("57.00"), nu in bani ca /cart.js sau /products/*.js. Impartirea la 100 afisa
     0.57 lei in loc de 57 (audit 2026-09-10). Acceptam ambele forme ca sa nu se
     rupa daca sursa se schimba: string = lei, number = bani. */
  function formatPrice(value) {
    if (value == null || value === '') return '';
    const lei = typeof value === 'string' ? parseFloat(value) : value / 100;
    if (isNaN(lei)) return '';
    const txt = Number.isInteger(lei) ? String(lei) : lei.toFixed(2).replace('.', ',');
    return txt + ' lei';
  }

  function renderProductCard(product) {
    const image = product.featured_image && product.featured_image.url
      ? product.featured_image.url
      : (product.image || '');
    const price = formatPrice(product.price);

    return `
      <a class="search-modal__result" href="${escapeHtml(product.url)}" data-search-result>
        <div class="search-modal__result-thumb">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="60" height="75">` : ''}
        </div>
        <div class="search-modal__result-body">
          <span class="search-modal__result-vendor">${escapeHtml(product.vendor || '')}</span>
          <span class="search-modal__result-title">${escapeHtml(product.title)}</span>
        </div>
        <span class="search-modal__result-price">${escapeHtml(price)}</span>
      </a>
    `;
  }

  function renderCollectionLink(coll) {
    return `
      <a class="search-modal__result-collection" href="${escapeHtml(coll.url)}" data-search-result>
        <span class="search-modal__result-collection-label">${escapeHtml(coll.title)}</span>
        <span class="search-modal__result-collection-arrow" aria-hidden="true">&rarr;</span>
      </a>
    `;
  }

  function renderQueryLink(q) {
    const url = '/search?q=' + encodeURIComponent(q.text) + '&type=product';
    return `
      <a class="search-modal__result-query" href="${escapeHtml(url)}" data-search-result>
        <span aria-hidden="true">↗</span>
        <span>${escapeHtml(q.text)}</span>
      </a>
    `;
  }

  function renderResults(data, query) {
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
    const queries = (data.resources && data.resources.results && data.resources.results.queries) || [];

    if (products.length === 0 && collections.length === 0 && queries.length === 0) {
      results.innerHTML = `
        <div class="search-modal__no-results">
          <p class="search-modal__no-results-heading">Nimic pentru &laquo;${escapeHtml(query)}&raquo;.</p>
          <p class="search-modal__no-results-copy">Încearcă alt termen sau scoate diacriticele.</p>
        </div>
      `;
      return;
    }

    let html = '';

    if (products.length > 0) {
      html += '<section class="search-modal__group" aria-label="Cafele">';
      html += '<h3 class="search-modal__group-heading">Cafele</h3>';
      html += '<div class="search-modal__group-list">';
      products.forEach(p => { html += renderProductCard(p); });
      html += '</div>';
      html += '</section>';
    }

    if (collections.length > 0) {
      html += '<section class="search-modal__group" aria-label="Colecții">';
      html += '<h3 class="search-modal__group-heading">Colecții</h3>';
      html += '<div class="search-modal__group-list search-modal__group-list--collections">';
      collections.forEach(c => { html += renderCollectionLink(c); });
      html += '</div>';
      html += '</section>';
    }

    if (queries.length > 0) {
      html += '<section class="search-modal__group" aria-label="Sugestii">';
      html += '<h3 class="search-modal__group-heading">Sugestii</h3>';
      html += '<div class="search-modal__group-list search-modal__group-list--queries">';
      queries.forEach(q => { html += renderQueryLink(q); });
      html += '</div>';
      html += '</section>';
    }

    html += `<a class="search-modal__see-all" href="/search?q=${encodeURIComponent(query)}&type=product">
      Vezi toate rezultatele pentru &laquo;${escapeHtml(query)}&raquo;
      <span aria-hidden="true">&rarr;</span>
    </a>`;

    results.innerHTML = html;
  }

  function showInitialEmpty() {
    if (emptyState && !results.contains(emptyState)) {
      results.innerHTML = '';
      results.appendChild(emptyState);
    }
  }

  async function performSearch(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    try {
      const url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(query)
        + '&resources[type]=product,collection,query'
        + '&resources[limit]=' + RESOURCE_LIMIT
        + '&resources[options][unavailable_products]=last';

      const res = await fetch(url, { signal: abortController.signal });
      if (!res.ok) throw new Error('Search request failed: ' + res.status);

      const data = await res.json();
      renderResults(data, query);
    } catch (err) {
      if (err.name === 'AbortError') return;
      results.innerHTML = '<p class="search-modal__error">Ceva nu a mers. Reîncearcă.</p>';
    }
  }

  const debouncedSearch = debounce(performSearch, DEBOUNCE_MS);

  function onInput() {
    const query = input.value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      lastQuery = '';
      showInitialEmpty();
      return;
    }
    if (query === lastQuery) return;
    lastQuery = query;
    debouncedSearch(query);
  }

  function openModal() {
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    document.body.classList.add('is-search-modal-open');
    requestAnimationFrame(() => input.focus());
  }

  function closeModal() {
    if (typeof modal.close === 'function') {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }
    document.body.classList.remove('is-search-modal-open');
    if (abortController) abortController.abort();
  }

  function init() {
    modal = document.querySelector('[data-search-modal]');
    if (!modal) return;

    form = modal.querySelector('[data-search-modal-form]');
    input = modal.querySelector('[data-search-modal-input]');
    closeBtn = modal.querySelector('[data-search-modal-close]');
    results = modal.querySelector('[data-search-modal-results]');
    emptyState = modal.querySelector('[data-search-modal-empty]');

    document.addEventListener('search:open', openModal);
    closeBtn.addEventListener('click', closeModal);

    modal.addEventListener('click', function (e) {
      const rect = form.getBoundingClientRect();
      const clickedOnBackdrop = e.target === modal
        || (e.clientY < rect.top || e.clientY > rect.bottom || e.clientX < rect.left || e.clientX > rect.right);
      if (clickedOnBackdrop) closeModal();
    });

    modal.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeModal();
    });

    input.addEventListener('input', onInput);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
